import {Fragment, FC, useCallback, useMemo} from 'react';
import {Form, Grid, Header, SemanticWIDTHS} from 'semantic-ui-react';
import {
  map, find, filter, get, pullAllWith, isMatch, isString, mapValues, keyBy, isNil, castArray, transform,
} from 'lodash';
import {DropdownInput, FetchDataError, GenericErrors, FormFragment, ValueInputRendererType} from 'apstra-ui-common';

import MultiParagraphText from '../../components/MultiParagraphText';
import {castObjectValuesToArray} from '../../processErrors';
import {MarkdownDescription} from './MarkdownDescription';

type PredefinedEntityFormProps = {
  label?: string;
  entity: any;
  entitiesByName: Record<string, any>;
  entityChoiceDisabled?: boolean;
  entityName?: string;
  parameters: Record<string, any>;
  actionInProgress?: boolean;
  errors?: any[];
  renderers?: ValueInputRendererType[];
  setEntityName: (entityName: string) => void;
  setParameter: (name: string, value: any) => void;
  columnWidths?: [SemanticWIDTHS, SemanticWIDTHS];
}

const PredefinedEntityForm: FC<PredefinedEntityFormProps> = ({
  label, actionInProgress, entityChoiceDisabled,
  entity, entityName, entitiesByName,
  parameters, errors, renderers,
  setEntityName, setParameter,
  columnWidths: [leftColumnWidth, rightColumnWidth] = [8, 8]
}) => {
  const genericErrors = useMemo(() => filter(errors, {type: 'generic'}), [errors]);
  const httpError = useMemo(() => find(errors, {type: 'http'}), [errors]);
  const propertyErrorsByKey = useMemo(
    () => mapValues(
      keyBy(filter(errors, {type: 'property'}), 'propertyName'),
      (error) => isNil(error.message) ? castObjectValuesToArray(error.propertyErrors) : castArray(error.message)
    ),
    [errors]
  );

  const propertyNamesBySection = useMemo(
    () => transform(entity?.sections, (result, propertyNames, sectionName) => {
      for (const propertyName of propertyNames) result[propertyName] = sectionName;
    }, {}),
    [entity]
  );

  const formSchemasBySection = useMemo(
    () => {
      const requiredPropetyNames = new Set(get(entity, ['schema', 'required'], []));
      const properties = get(entity, ['schema', 'properties'], {});
      return transform(properties, (result, propertySchema, propertyName) => {
        const sectionName = propertyNamesBySection[propertyName] ?? '';
        (result[sectionName] ??= []).push({
          name: propertyName,
          schema: propertySchema,
          required: propertyName === 'label' || requiredPropetyNames.has(propertyName),
        });
      }, {});
    },
    [entity, propertyNamesBySection]
  );

  const setEntityNameAndClearErrors = useCallback((entityName) => {
    setEntityName(entityName);
    if (errors) errors.length = 0;
  }, [setEntityName, errors]);

  const setParameterAndClearErrors = useCallback((name, value) => {
    setParameter(name, value);
    if (errors) pullAllWith(errors, [{type: 'property', propertyName: name}, {type: 'generic'}], isMatch);
  }, [setParameter, errors]);

  return (
    <Grid divided>
      <Grid.Column width={leftColumnWidth}>
        <Form>
          <DropdownInput
            search
            schema={{title: label}}
            errors={propertyErrorsByKey.id}
            disabled={entityChoiceDisabled || actionInProgress}
            required
            value={entityName}
            options={map(entitiesByName, (entity, entityName) => ({
              key: entityName,
              value: entityName,
              text: get(entity, ['schema', 'properties', 'label', 'default'], entityName),
            }))}
            onChange={setEntityNameAndClearErrors}
          />
          {map(formSchemasBySection, (formSchema, sectionName) => (
            <Fragment key={sectionName}>
              {sectionName &&
                <Header dividing as='h3'>{sectionName}</Header>
              }
              <FormFragment
                schema={formSchema}
                values={parameters}
                errors={propertyErrorsByKey}
                renderers={renderers}
                disabled={actionInProgress}
                onChange={setParameterAndClearErrors}
                fieldProps={{DescriptionComponent: MarkdownDescription}}
              />
            </Fragment>
          ))}
        </Form>
      </Grid.Column>
      <Grid.Column width={rightColumnWidth}>
        {entity && entity.description && (
          isString(entity.description) ?
            <MultiParagraphText asMarkdown text={entity.description} />
          :
            entity.description
        )}
      </Grid.Column>
      {!!genericErrors.length &&
        <Grid.Column width={16}>
          <GenericErrors errors={map(genericErrors, 'message')} />
        </Grid.Column>
      }
      {httpError &&
        <Grid.Column width={16}>
          <FetchDataError error={httpError.error} />
        </Grid.Column>
      }
    </Grid>
  );
};

export default PredefinedEntityForm;
